import * as React from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { PanelLeftClose, PanelLeftOpen } from "Components/ui/icons";

import {
  Button,
  ErrorBoundary,
  IconButton,
  Logo,
  LogoMark,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  Tooltip,
} from "Components/ui";
import { useServerStatus, type ServerStatus } from "hooks/useServerStatus";
import ProfileSheet from "Routes/Profile/ProfileSheet";
import { cn } from "utils/cn";
import { isProfileOpen, withoutProfile } from "utils/profileSheet";
import { CommandPalette, useCommandPaletteShortcut } from "./CommandPalette";
import { SidebarNav } from "./SidebarNav";
import { Topbar } from "./Topbar";

const COLLAPSED_KEY = "sap-sidebar-collapsed";

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch (err) {
    return false;
  }
};

const STATUS_COPY: Record<ServerStatus, { label: string; dot: string }> = {
  checking: { label: "Checking API…", dot: "bg-subtle-foreground" },
  online: { label: "API online", dot: "bg-success" },
  offline: { label: "API unreachable", dot: "bg-danger" },
};

const ServerStatusIndicator: React.FC<{ compact: boolean }> = ({ compact }) => {
  const status = useServerStatus();
  const copy = STATUS_COPY[status];
  const dot = (
    <span role="status" className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className="relative flex h-2 w-2">
        {status === "online" && (
          <span className="absolute inset-0 rounded-full bg-success/50 motion-safe:animate-ping" />
        )}
        <span className={cn("relative h-2 w-2 rounded-full", copy.dot)} />
      </span>
      <span className={cn(compact && "sr-only")}>{copy.label}</span>
    </span>
  );
  return compact ? (
    <Tooltip content={copy.label} side="right">
      <span className="flex h-8 w-8 items-center justify-center">{dot}</span>
    </Tooltip>
  ) : (
    dot
  );
};

/**
 * Signed-in layout: collapsible sidebar on a tinted ground (a drawer on small screens), the page in
 * a floating panel with a sticky top bar, and the Cmd+K command palette. Pages render inside
 * <main>, usually wrapped in <Page>.
 */
export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsed] = React.useState(readCollapsed);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const location = useLocation();
  const history = useHistory();
  const { pathname } = location;
  const profileOpen = isProfileOpen(location.search);

  const togglePalette = React.useCallback(() => setPaletteOpen((open) => !open), []);
  useCommandPaletteShortcut(togglePalette);

  React.useEffect(() => setMenuOpen(false), [pathname]);

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      try {
        window.localStorage.setItem(COLLAPSED_KEY, value ? "0" : "1");
      } catch (err) {
        // Storage blocked: the choice still applies until reload.
      }
      return !value;
    });
  };

  return (
    <div className="min-h-screen bg-background text-foreground lg:bg-sidebar">
      <a
        href="#main"
        className="sr-only z-50 rounded-control bg-primary px-3 py-2 text-sm font-medium text-primary-foreground focus:not-sr-only focus:fixed focus:left-4 focus:top-3"
      >
        Skip to content
      </a>

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 hidden flex-col transition-[width] duration-base ease-standard lg:flex",
          collapsed ? "w-[4.25rem]" : "w-60"
        )}
      >
        {/* mt-2 matches the content panel's lg:my-2, so the logo lines up with the top bar. */}
        <div
          className={cn(
            "mt-2 flex h-14 shrink-0 items-center",
            collapsed ? "justify-center" : "justify-between pl-4 pr-2"
          )}
        >
          {collapsed ? (
            <Tooltip content="Expand sidebar" side="right">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleCollapsed}
                aria-label="Expand sidebar"
                className="group/expand relative hover:bg-surface/70"
              >
                {/* The kit sizes icons inside buttons to 16px; the mark keeps its 28px. */}
                <LogoMark className="!h-7 !w-7 transition-opacity duration-fast group-hover/expand:opacity-0 group-focus-visible/expand:opacity-0" />
                <PanelLeftOpen
                  aria-hidden="true"
                  className="absolute text-muted-foreground opacity-0 transition-opacity duration-fast group-hover/expand:opacity-100 group-focus-visible/expand:opacity-100"
                />
              </Button>
            </Tooltip>
          ) : (
            <>
              <Link
                to="/dashboard"
                aria-label="Super Admin dashboard"
                // flex, not inline: an inline link adds baseline space under the logo.
                className="flex items-center rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Logo />
              </Link>
              <IconButton
                size="sm"
                label="Collapse sidebar"
                icon={<PanelLeftClose />}
                onClick={toggleCollapsed}
                className="text-subtle-foreground hover:bg-surface/70 hover:text-foreground"
              />
            </>
          )}
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 pb-4 pt-3">
          <SidebarNav collapsed={collapsed} />
        </div>
        <div
          className={cn("flex shrink-0 items-center px-5 py-3", collapsed && "justify-center px-0")}
        >
          <ServerStatusIndicator compact={collapsed} />
        </div>
      </aside>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="max-w-[18rem] bg-sidebar">
          <SheetHeader title={<Logo />} />
          <SheetBody className="px-3">
            <SidebarNav onNavigate={() => setMenuOpen(false)} />
          </SheetBody>
          <div className="px-5 py-3">
            <ServerStatusIndicator compact={false} />
          </div>
        </SheetContent>
      </Sheet>

      <div
        className={cn(
          "transition-[padding] duration-base ease-standard",
          collapsed ? "lg:pl-[4.25rem]" : "lg:pl-60"
        )}
      >
        {/* Content floats as a panel on the sidebar ground (large screens). overflow-clip, not
            hidden, so the sticky top bar keeps sticking. */}
        <div className="flex min-h-screen flex-col overflow-clip bg-background lg:my-2 lg:mr-2 lg:min-h-[calc(100vh-1rem)] lg:rounded-xl lg:border lg:border-border lg:shadow-xs">
          <Topbar onOpenMenu={() => setMenuOpen(true)} onOpenSearch={() => setPaletteOpen(true)} />
          <main id="main" tabIndex={-1} className="flex-1 focus:outline-none">
            <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
          </main>
        </div>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <ProfileSheet
        open={profileOpen}
        onOpenChange={(open) => {
          if (!open) history.replace(withoutProfile(location));
        }}
      />
    </div>
  );
};

export default AppShell;
